/*
 * Copyright (c) 2025 Frayo and HomeChef360. All Rights Reserved.
 *
 * CONFIDENTIAL & PROPRIETARY
 * This software and its source code are the confidential and proprietary
 * information of Frayo and HomeChef360. Unauthorized copying, modification,
 * distribution, disclosure, or use of this code, in whole or in part, via any
 * medium, is strictly prohibited without prior written consent.
 *
 * Violations will be subject to legal action in the State of Florida and may
 * also be pursued under applicable U.S. federal law.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteCertificate } from "../../core/_requests";
import { toast } from "react-toastify";

interface certificateProps {
    certificateList : any[]
}


const CertificateSettings : React.FC<certificateProps> = ({ certificateList }) => {
  const navigate = useNavigate();

  const [isExpanded, setIsExpanded] = useState(false);

  // Toggle function to expand or collapse content
  const toggleReadMore = () => {
    setIsExpanded((prev) => !prev);
  };
  
  const addcertificate = () => {
    navigate("/settings/add-certificate");
  };

  const editcertificate = (certificateId: any) => {
    navigate(`/settings/edit-certificate/${certificateId}`);
  };

  const handleDeleteCertificate = (certificateId: any) => {
    
    deleteCertificate(certificateId)
      .then((res) => {
                if (res.status === true) {
          navigate("/settings");
          toast.success(res?.message, { autoClose: 5000 });
        } else {
          toast.error(res?.message);
          // Redirect to the settings page
          navigate("/settings");
        }
      })
      .catch((error) => {
        console.error("Error fetching categories:", error);
      });
  };

  const getIconByFileType = (fileUrl: any) => {
    const extension = fileUrl.split(".").pop().toLowerCase();
    if (["jpg", "png", "webp"].includes(extension)) {
      return "🖼️"; // Use a proper image icon here
    } else if (["pdf", "doc", "docx"].includes(extension)) {
      return "📄"; // Use a proper document icon here
    } else {
      return "📁"; // Default icon for other file types
    }
  };
  return (
    <div>
      {certificateList?.length > 0 ? (
        <div className="your-cuisine mb-3">
          <div className="row">
            {certificateList?.map((certificate: any) => (
              <div className="col-md-12 col-sm-6 mb-4" key={certificate?.uuid}>
                <div className="card h-100 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="card-title mb-0">{certificate?.name}</h6>
                      <div className="d-flex align-items-center justify-content-end">
                        <span
                          className="text-primary cursor-pointer me-3 d-flex align-items-center"
                          onClick={() => editcertificate(certificate?.uuid)}
                        >
                          <i className="material-symbols-outlined me-1">edit</i>
                          Edit
                        </span>

                        <span
                          className="text-danger cursor-pointer d-flex align-items-center"
                          onClick={() =>
                            handleDeleteCertificate(certificate?.uuid)
                          }
                        >
                          <i className="material-symbols-outlined me-1">
                            delete
                          </i>
                          Delete
                        </span>
                      </div>
                      <p className="card-text">
                        <a
                          href={certificate.certificate}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-link"
                        >
                          {getIconByFileType(certificate?.certificate)} View
                          Certificate
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="empty-certificate-wrap mb-3">
          <div className="card shadow-sm p-3">
            <div className="head-line">
              <h6 className="fw-med mb-0 mb-1 text-center">
                Become a Certified Chef!
              </h6>
              <a
                href="#"
                className="text-link"
                onClick={addcertificate}
              >
                Add Certificate
              </a>
            </div>

            <p className="mb-2">
              At Frayo, we prioritize the safety and quality of the meals
              you serve. That's why we recommend all our frayo chefs to complete
              a Food Protection & Safety Certification!
            </p>
            {/* Conditionally render additional content */}
            {isExpanded && (
              <>
                <p>
                  This certification ensures that every dish delivered from our
                  platform meets the highest food safety standards, giving
                  customers peace of mind while enjoying your delicious
                  creations.
                </p>
                <p>
                  As a certified chef, your certificate status will be
                  prominently displayed next to your profile on Frayo.
                  Remember, this creates credibility among your customers
                  regarding your seriousness and commitment.
                </p>
                <p>
                  Below are some nationwide resources across the USA that
                  provide training and certification on food safety. Please
                  review and get certified from any one of the listed accredited
                  bodies.
                </p>
                <ul className="list-group">
                  <li className="list-group-item">
                    <a
                      href="https://www.aaafoodhandler.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      AAA Food Safety
                    </a>
                  </li>
                  <li className="list-group-item">
                    <a
                      href="https://worldfoodsafety.org/fpm"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      APS Culinary Dynamics
                    </a>
                  </li>
                  <li className="list-group-item">
                    <a
                      href="https://www.statefoodsafety.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Certus/State Food Safety
                    </a>
                  </li>
                  <li className="list-group-item">
                    <a
                      href="https://www.educlasses.org"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      DSBWorldWide
                    </a>
                  </li>
                  <li className="list-group-item">
                    <a
                      href="https://www.360training.com/learn2serve"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn2Serve
                    </a>
                  </li>
                  <li className="list-group-item">
                    <a
                      href="https://myfoodservicelicense.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      My Food Service License
                    </a>
                  </li>
                  <li className="list-group-item">
                    <a
                      href="http://www.nrfsp.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      National Registry of Food Safety Professionals
                    </a>
                  </li>
                  <li className="list-group-item">
                    <a
                      href="http://www.servsafe.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      National Restaurant Association Solutions
                    </a>
                  </li>
                  <li className="list-group-item">
                    <a
                      href="https://www.trust20.co"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Relish Works, Inc
                    </a>
                  </li>
                  <li className="list-group-item">
                    <a
                      href="https://alwaysfoodsafe.com"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      The Always Food Safe Company
                    </a>
                  </li>
                  <li className="list-group-item">
                    <a
                      href="https://www.userve.com/us"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Userve
                    </a>
                  </li>
                </ul>
                <p className="mt-3">
                  Keep cooking safely, and let’s continue to share the joy of
                  homemade meals across the community!
                </p>
              </>
            )}
            {/* Link to toggle read more/less */}
            <a
              href="#"
              onClick={toggleReadMore}
              className={`text-link ${!isExpanded ? "text-danger" : ""}`}
            >
              {isExpanded ? "Read less" : "Read more"}
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateSettings;
